import React, { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { displayBundesland, LoginRequest } from '@radevents/shared';
import { usePageTitle } from '../hooks/usePageTitle';
import { Spinner } from '../components/ui';
import { AuthError } from '../services/authService';
import { umamiTrack } from '../services/analytics';
import { brand } from '../config/brand';
import { PrivacyNote } from '../components/PrivacyNote';
import radsportMarkWhite from '../assets/brand/radsport-mark-white.svg';

const FONT_HEAD = "'Archivo',sans-serif";
const FONT_MONO = "'Space Mono',monospace";
const FONT_BODY = "'Hanken Grotesk',sans-serif";

type Mode = 'code' | 'password';
type CodeStep = 'identifier' | 'verify';

/**
 * Anmelden und Registrieren sind derselbe Ablauf: E-Mail, Einmal-Code, fertig. Der Code legt ein
 * unbekanntes Konto selbst an (`UserService.loginWithVerifiedEmail`), deshalb ist `/register` nur
 * diese Seite mit anderen Überschriften, ohne Passwort-Umschalter. Bis zum 20.10.2026 stand dort
 * noch das Formular mit Passwort und Bestätigungslink aus der Zeit vor dem Code-Login.
 */
const Login: React.FC<{ variant?: 'login' | 'register' }> = ({ variant = 'login' }) => {
  const isRegister = variant === 'register';
  usePageTitle(isRegister ? 'Konto erstellen' : 'Anmelden');
  const { login, requestEmailCode, loginWithEmailCode, resendConfirmation, isAuthenticated, isLoading, user: authUser } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [mode, setMode] = useState<Mode>('code');
  const [codeStep, setCodeStep] = useState<CodeStep>('identifier');

  // Passwordless (default): one field takes an e-mail or username, then a 6-digit code.
  const [identifier, setIdentifier] = useState(searchParams.get('email') ?? '');
  const [code, setCode] = useState('');

  // Password fallback
  const [credentials, setCredentials] = useState<LoginRequest>({ username: '', password: '' });

  const [error, setError] = useState<string | null>(null);
  // Adresse aus einer 403-Antwort "E-Mail noch nicht bestaetigt" — schaltet den Neuversand frei.
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [shakeKey, setShakeKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Sekunden bis der Code erneut gesendet werden darf (Cooldown gegen Mail-Spam).
  const [resendCooldown, setResendCooldown] = useState(0);
  const [codeResent, setCodeResent] = useState(false);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(s => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const defaultRedirect = (authUser?.role === 'ADMIN' || authUser?.role === 'MODERATOR') ? '/admin/start' : '/';
  /**
   * Rückweg nach dem Anmelden.
   *
   * Zwei Wege führen hierher: ein abgefangener Aufruf einer geschützten Seite (`location.state`,
   * von ProtectedRoute gesetzt) und ein Link, der bewusst hierher schickt und weiß, wohin es
   * danach gehen soll (`?redirect=`) — etwa „Ich bin der Veranstalter" auf einer Veranstalterseite.
   * Der zweite Fall wurde bislang ignoriert; wer den Knopf drückte, landete nach dem Anmelden auf
   * der Startseite und musste die Seite selbst wiederfinden.
   *
   * Nur seiteneigene Ziele: ein Wert, der nicht mit genau einem `/` beginnt, könnte auf eine
   * fremde Domain zeigen (offene Weiterleitung) und wird verworfen.
   */
  const redirectParam = searchParams.get('redirect');
  const safeRedirect = redirectParam && /^\/(?!\/)/.test(redirectParam) ? redirectParam : null;
  const from = safeRedirect || (location.state as any)?.from?.pathname || defaultRedirect;

  // „Ich bin der Veranstalter" hat hierher geschickt. Der Hinweis auf die eigene Domain gehört
  // genau hierhin: NACH dem Anmelden lässt sich die Adresse nur noch mit neuer Bestätigung
  // wechseln, und die Domain ist der stärkste Beleg, den wir automatisch prüfen können.
  const claimOrganizer = searchParams.get('veranstalter');
  // Aus der URL, also nicht vertrauenswürdig: nur etwas, das wie ein Hostname aussieht, wird
  // angezeigt — sonst stünde beliebiger fremder Text in unserer Oberfläche.
  const claimDomainRaw = searchParams.get('domain') ?? '';
  const claimDomain = /^[a-z0-9.-]{3,80}\.[a-z]{2,}$/i.test(claimDomainRaw) ? claimDomainRaw : null;
  // Aus „Events per Mail" mit gewählter Region: der Code-Login schaltet die Regions-Mails dafür
  // ein. Das Backend nimmt nur eine bekannte Region an.
  const digestBundesland = searchParams.get('bundesland') || undefined;

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  if (isLoading) {
    return <Spinner fullPage />;
  }

  const fail = (message: string) => {
    setError(message);
    setShakeKey(k => k + 1);
  };

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      await requestEmailCode(identifier.trim());
      setCodeStep('verify');
      setResendCooldown(60);
      setCodeResent(false);
    } catch (err) {
      fail(err instanceof Error ? err.message : 'Code konnte nicht gesendet werden.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0 || isSubmitting) return;
    setError(null);
    setCodeResent(false);
    try {
      await requestEmailCode(identifier.trim());
      setResendCooldown(60);
      setCodeResent(true);
    } catch (err) {
      fail(err instanceof Error ? err.message : 'Code konnte nicht gesendet werden.');
    }
  };

  // Validiert den Code. Wird sowohl vom Submit-Button als auch automatisch beim Erreichen von
  // 6 Ziffern (Tippen/Einfügen) aufgerufen. Der Wert wird explizit übergeben, weil der State
  // beim Auto-Submit aus onChange noch nicht aktualisiert ist.
  const submitCode = async (value: string) => {
    if (value.length !== 6 || isSubmitting) return;
    setError(null);
    setIsSubmitting(true);
    try {
      await loginWithEmailCode(identifier.trim(), value, { digestBundesland, ...(typeof from === 'string' && from.startsWith('/winterchallenge') ? { signupSource: 'winterchallenge' } : {}) });
      if (isRegister) umamiTrack('registrierung', { quelle: 'register-seite' });
    } catch (err) {
      fail(err instanceof Error ? err.message : 'Code ungültig oder abgelaufen');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyCode = (e: React.FormEvent) => {
    e.preventDefault();
    submitCode(code.trim());
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setUnverifiedEmail(null);
    setIsSubmitting(true);
    try {
      await login(credentials);
    } catch (err) {
      if (err instanceof AuthError && err.code === 'EMAIL_NOT_VERIFIED') {
        setUnverifiedEmail(err.email || credentials.username);
        setResendState('idle');
      }
      fail(err instanceof Error ? err.message : 'Ungültiger Benutzername oder Passwort');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (!unverifiedEmail) return;
    setResendState('sending');
    await resendConfirmation(unverifiedEmail);
    setResendState('sent');
  };

  const switchMode = (next: Mode) => {
    setMode(next);
    setError(null);
    setUnverifiedEmail(null);
    setCodeStep('identifier');
    setCode('');
    setResendCooldown(0);
    setCodeResent(false);
  };

  // We only echo the address the user typed themselves (an e-mail); for a username we must not
  // reveal the account's e-mail, so the message stays generic (no account enumeration).
  const identifierIsEmail = identifier.includes('@');

  const labelStyle: React.CSSProperties = {
    display: 'block', fontFamily: FONT_MONO, fontSize: 11, letterSpacing: '0.12em',
    textTransform: 'uppercase', color: '#9A9494', marginBottom: 8,
  };
  const inputStyle: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', background: '#F5F3F3', border: '1px solid #E5E1E1',
    borderRadius: 11, padding: '13px 14px', fontFamily: FONT_BODY, fontSize: 15, color: '#2C2C2C', outline: 'none',
  };
  const linkBtnStyle: React.CSSProperties = {
    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
    fontFamily: FONT_BODY, fontSize: 14, fontWeight: 700, color: '#922C57',
  };

  const primaryBtn = (label: string, loadingLabel: string): React.ReactNode => (
    <button
      type="submit"
      disabled={isSubmitting}
      style={{
        width: '100%', marginTop: 20, background: '#922C57', color: '#fff', border: 'none',
        padding: 15, borderRadius: 12, fontFamily: FONT_BODY, fontWeight: 700, fontSize: 16,
        cursor: isSubmitting ? 'not-allowed' : 'pointer', opacity: isSubmitting ? 0.6 : 1,
        boxShadow: '0 8px 20px rgba(146,44,87,0.30)',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
      }}
    >
      {isSubmitting ? (
        <>
          <svg className="animate-spin" style={{ height: 16, width: 16, color: '#fff' }} fill="none" viewBox="0 0 24 24" aria-hidden="true">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          {loadingLabel}
        </>
      ) : label}
    </button>
  );

  const errorBox = error && (
    <div style={{
      marginTop: 16, borderRadius: 11, padding: '12px 14px',
      background: 'rgba(168,47,90,0.10)', display: 'flex', alignItems: 'flex-start', gap: 10,
    }}>
      <svg style={{ width: 20, height: 20, flexShrink: 0, marginTop: 1, color: '#A82F5A' }} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
      </svg>
      <div>
        <p style={{ fontSize: 14, fontWeight: 600, color: '#A82F5A', margin: 0 }}>{error}</p>
        {unverifiedEmail && (
          <button
            type="button"
            onClick={handleResendConfirmation}
            disabled={resendState !== 'idle'}
            style={{
              background: 'none', border: 'none', padding: 0, marginTop: 6,
              cursor: resendState === 'idle' ? 'pointer' : 'default',
              fontFamily: FONT_BODY, fontSize: 13, fontWeight: 700, color: '#922C57',
              textDecoration: 'underline',
            }}
          >
            {resendState === 'idle' && 'Bestätigungslink erneut senden'}
            {resendState === 'sending' && 'Wird gesendet…'}
            {resendState === 'sent' && 'Gesendet — schau in dein Postfach'}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div style={{
      minHeight: '100vh', background: '#2C2C2C', fontFamily: FONT_BODY,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      position: 'relative', overflow: 'hidden',
    }}>
      {/* Deko-Formen (rein dekorativ, hinter dem Inhalt, dürfen anschneiden) */}
      <div aria-hidden="true" style={{
        position: 'absolute', right: -90, top: 10, width: 280, height: 280,
        background: '#922C57', opacity: 0.22, transform: 'rotate(45deg)', borderRadius: 54,
      }} />
      <div aria-hidden="true" style={{
        position: 'absolute', left: -80, bottom: 120, width: 200, height: 200,
        border: '2px solid rgba(194,80,120,0.30)', transform: 'rotate(45deg)', borderRadius: 38,
      }} />
      <div className="animate-fade-up" style={{ width: '100%', maxWidth: 400, position: 'relative', zIndex: 1 }}>
        {/* Brand mark */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 24 }}>
          <div style={{
            width: 64, height: 64, borderRadius: 18, background: '#922C57',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 14px 30px rgba(146,44,87,0.45)',
          }}>
            <img src={radsportMarkWhite} alt="" width={44} height={44} style={{ display: 'block', objectFit: 'contain' }} />
          </div>
          <div style={{ fontFamily: FONT_MONO, fontSize: 11, letterSpacing: '0.2em', color: '#9A9494', marginTop: 18 }}>
            {'// '}RADSPORT<span style={{ color: '#C25078' }}> EVENTS</span>
          </div>
          <h1 style={{
            fontFamily: FONT_HEAD, fontWeight: 800, fontSize: 32, lineHeight: 1,
            letterSpacing: '-0.015em', color: '#fff', margin: '10px 0 0', textAlign: 'center',
          }}>{isRegister ? 'Konto erstellen' : 'Willkommen zurück'}</h1>
          <p style={{ fontFamily: FONT_BODY, fontSize: 14, color: '#C4BFBF', margin: '9px 0 0', textAlign: 'center' }}>
            {isRegister && digestBundesland
              ? <>Danach bekommst du die Events in <strong style={{ color: '#fff' }}>{displayBundesland(digestBundesland)}</strong> per E-Mail.</>
              : isRegister ? `Kostenlos registrieren bei ${brand.appName}` : 'Schön, dass du wieder am Start bist.'}
          </p>
        </div>

        {claimOrganizer && (
          <div style={{
            background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.18)',
            borderRadius: 14, padding: '14px 16px', marginBottom: 16,
          }}>
            <p style={{ fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '0.14em', color: '#C25078', margin: '0 0 6px' }}>
              VERANSTALTER-ZUGANG
            </p>
            <p style={{ fontFamily: FONT_BODY, fontSize: 13.5, lineHeight: 1.55, color: '#E8E4E4', margin: 0 }}>
              {isRegister ? 'Leg dein Konto an' : 'Melde dich an'}, danach geht es zurück zu deinem Antrag. Am schnellsten prüfen wir ihn,
              wenn dein Konto auf einer E-Mail-Adresse eurer eigenen Domain läuft
              {claimDomain ? <> (also <strong style={{ color: '#fff' }}>@{claimDomain}</strong>)</> : null}.
            </p>
          </div>
        )}

        {/* Form card */}
        <div
          key={shakeKey}
          className={error ? 'animate-shake' : undefined}
          style={{
            background: '#fff', border: '1px solid #E5E1E1', borderRadius: 16, padding: 24,
            boxShadow: '0 4px 24px -4px rgba(0,0,0,0.08)',
          }}
        >
          {/* Default: passwordless code login */}
          {mode === 'code' && codeStep === 'identifier' && (
            <form onSubmit={handleRequestCode}>
              <label htmlFor="identifier" style={labelStyle}>{isRegister ? 'E-Mail-Adresse' : 'E-Mail oder Benutzername'}</label>
              <input
                id="identifier" name="identifier" type={isRegister ? 'email' : 'text'} required autoFocus style={inputStyle}
                placeholder={isRegister ? 'du@beispiel.de' : 'E-Mail oder Benutzername'}
                autoCapitalize="none" autoCorrect="off" autoComplete="email"
                value={identifier}
                onChange={e => setIdentifier(e.target.value)}
                disabled={isSubmitting}
              />
              <p style={{ fontSize: 13, color: '#6A6A6A', margin: '12px 0 0', lineHeight: 1.5 }}>
                Wir senden dir einen 6-stelligen Code per E-Mail. Kein Passwort nötig.
{' '}<PrivacyNote withAccount={!isRegister} />
              </p>
              {errorBox}
              {primaryBtn('Code senden →', 'Sende…')}
            </form>
          )}

          {mode === 'code' && codeStep === 'verify' && (
            <form onSubmit={handleVerifyCode}>
              <label htmlFor="code" style={labelStyle}>Code aus der E-Mail</label>
              <input
                id="code" name="code" type="text" inputMode="numeric" autoComplete="one-time-code"
                pattern="[0-9]*" maxLength={6} required autoFocus
                style={{ ...inputStyle, letterSpacing: '0.35em', fontSize: 22, textAlign: 'center', fontFamily: FONT_MONO }}
                placeholder="000000"
                value={code}
                onChange={e => {
                  const next = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setCode(next);
                  if (next.length === 6) submitCode(next);
                }}
                disabled={isSubmitting}
              />
              <p style={{ fontSize: 13, color: '#6A6A6A', margin: '12px 0 0', lineHeight: 1.5 }}>
                {identifierIsEmail
                  ? <>Code an <strong style={{ color: '#2C2C2C' }}>{identifier.trim()}</strong> gesendet.</>
                  : <>Wir haben dir einen Code per E-Mail geschickt.</>}
                {' '}
                <button type="button" onClick={() => { setCodeStep('identifier'); setError(null); }} style={{ ...linkBtnStyle, fontSize: 13 }}>
                  Ändern
                </button>
              </p>
              {errorBox}
              {primaryBtn(isRegister ? 'Konto erstellen' : 'Anmelden →', 'Prüfe…')}
              <div style={{ marginTop: 16, textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={resendCooldown > 0 || isSubmitting}
                  style={{
                    ...linkBtnStyle, fontSize: 13,
                    color: resendCooldown > 0 ? '#9A9494' : '#922C57',
                    cursor: resendCooldown > 0 ? 'default' : 'pointer',
                  }}
                >
                  {resendCooldown > 0 ? `Code erneut senden in ${resendCooldown}s` : 'Code erneut senden'}
                </button>
                {codeResent && resendCooldown > 0 && (
                  <p style={{ fontSize: 12, color: '#1F8A5B', margin: '6px 0 0' }}>Neuer Code gesendet.</p>
                )}
              </div>
            </form>
          )}

          {/* Fallback: password login */}
          {mode === 'password' && (
            <form onSubmit={handlePasswordSubmit}>
              <label htmlFor="username" style={labelStyle}>E-Mail oder Benutzername</label>
              <input
                id="username" name="username" type="text" required autoFocus style={inputStyle}
                placeholder="E-Mail oder Benutzername"
                autoCapitalize="none" autoCorrect="off"
                value={credentials.username}
                onChange={e => setCredentials(prev => ({ ...prev, username: e.target.value }))}
                disabled={isSubmitting}
              />
              <div style={{ marginTop: 16 }}>
                <label htmlFor="password" style={labelStyle}>Passwort</label>
                <input
                  id="password" name="password" type="password" required style={inputStyle}
                  placeholder="Passwort eingeben"
                  value={credentials.password}
                  onChange={e => setCredentials(prev => ({ ...prev, password: e.target.value }))}
                  disabled={isSubmitting}
                />
              </div>
              {errorBox}
              {primaryBtn('Anmelden →', 'Anmelden…')}
            </form>
          )}

          {/* Mode toggle — beim Registrieren nicht: ein Konto mit Passwort legt niemand mehr neu an. */}
          {!isRegister && <div style={{ marginTop: 20, paddingTop: 18, borderTop: '1px solid #EFEBEB', textAlign: 'center' }}>
            {mode === 'code' ? (
              <button type="button" onClick={() => switchMode('password')} style={linkBtnStyle}>
                Mit Passwort anmelden
              </button>
            ) : (
              <button type="button" onClick={() => switchMode('code')} style={linkBtnStyle}>
                Mit E-Mail-Code anmelden
              </button>
            )}
          </div>}
        </div>

        {/* Nur auf /register: der Weg zurück zum Anmelden mit Passwort, den es dort nicht gibt.
            Auf /login braucht es keinen Link zum Registrieren, der Code legt ein neues Konto selbst an. */}
        {isRegister && (
          <p style={{ marginTop: 22, textAlign: 'center', fontSize: 14, color: '#C4BFBF' }}>
            Bereits ein Konto?{' '}
            {/* Die Absicht (Veranstalter-Antrag samt Rückweg, Region) muss den Wechsel überleben. */}
            <Link
              to={`/login${window.location.search}`}
              state={location.state}
              style={{ fontWeight: 700, color: '#C25078', textDecoration: 'none' }}
            >
              Anmelden
            </Link>
          </p>
        )}
        <p style={{ marginTop: isRegister ? 10 : 22, textAlign: 'center', fontSize: 14 }}>
          <Link to="/" style={{ fontWeight: 600, color: '#C25078', textDecoration: 'none' }}>
            ← Zurück zu Events
          </Link>
        </p>

        {/* Footer-Links: mobil fehlt die Desktop-Footerleiste — diese Zeile stellt die
            Erreichbarkeit der Info-/Rechts-Seiten auch im Login her. */}
        <nav aria-label="Rechtliches und Infos" style={{
          marginTop: 20, display: 'flex', flexWrap: 'wrap', alignItems: 'center',
          justifyContent: 'center', gap: '6px 12px',
        }}>
          {[
            { to: '/ueber-uns', label: 'Über uns' },
            { to: '/impressum', label: 'Impressum' },
            { to: '/datenschutz', label: 'Datenschutz' },
            { to: '/fuer-partner', label: 'Für Partner' },
          ].map((link, i) => (
            // Punkt + Link als Paar, damit beim Umbruch kein Trennpunkt am Zeilenende hängt.
            <span key={link.to} style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
              {i > 0 && (
                <span aria-hidden="true" style={{
                  width: 3, height: 3, borderRadius: '50%', background: 'rgba(255,255,255,0.22)',
                }} />
              )}
              <Link to={link.to} style={{
                fontFamily: FONT_BODY, fontSize: 12, color: '#9A9494', textDecoration: 'none',
                padding: '14px 2px', // vergrößert das Touch-Target auf ~44px Höhe
              }}>
                {link.label}
              </Link>
            </span>
          ))}
        </nav>
      </div>
    </div>
  );
};

export default Login;
